// LowStockReport.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

const LowStockReport = () => {
  const [lowStockItems, setLowStockItems] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [productsList, setProductsList] = useState([]);

  const [productReorderMap, setProductReorderMap] = useState({});

  const [selectedProductId, setSelectedProductId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const printRef = useRef();

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) value = 0;
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currencyCode} ${Number(value).toFixed(2)}`;
    }
  }, [company]);

  const computeEffectiveMin = useCallback((item) => {
    const stockMin = Number(item?.min_qty || 0);
    if (stockMin > 0) return stockMin;

    const productId = String(item?.product_id?._id || item?.product_id || '');
    return Number(productReorderMap[productId] || 0);
  }, [productReorderMap]);

  const isLowStockItem = useCallback((item) => {
    const qty = Number(item?.current_qty ?? item?.available_qty ?? item?.quantity ?? 0);
    const min = computeEffectiveMin(item);
    return min > 0 && qty < min;
  }, [computeEffectiveMin]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setCompany(res.data);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/products`, {
        headers: getAuthHeaders(),
        params: { status: 'active', limit: 500 },
        timeout: 5000,
      });
      setProductsList(res.data.data || []);
    } catch (err) {
      setProductsList([]);
    }
  }, []);

  const fetchProductReorderMap = useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/products`, {
        headers: getAuthHeaders(),
        params: { status: 'active', limit: 5000 },
        timeout: 10000,
      });

      const list = res.data?.data || res.data?.items || res.data?.results || [];

      const map = {};
      list.forEach((p) => {
        const id = String(p._id || p.id || '');
        if (!id) return;
        const reorder = Number(
          p.reorder_level ?? p.reorder_qty ?? p.reorder_point ??
          p.min_qty ?? p.min_stock_level ?? p.minimum_stock ?? 0
        );
        map[id] = reorder;
      });
      setProductReorderMap(map);
    } catch (err) {
      setProductReorderMap({});
    }
  }, []);

  const fetchLowStock = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedProductId) params.productId = selectedProductId;

      const res = await axios.get(`${url}/api/inventorystock/low-stock`, {
        headers: getAuthHeaders(),
        params,
      });

      let items = res.data.data || [];

      items = items.map(item => {
        const currentQty = item.available_qty ?? item.quantity ?? 0;
        const avgCost = item.product_id?.avg_cost || item.avg_cost || 0;
        const totalValue = currentQty * avgCost;
        return {
          ...item,
          current_qty: currentQty,
          avg_cost: avgCost,
          total_value: totalValue,
          item_name: item.product_id?.product_name || 'Unknown',
        };
      });

      setLowStockItems(items);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load low stock report');
      setLowStockItems([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedProductId]);

  const itemsWithStatus = React.useMemo(() => {
    return lowStockItems.map(item => {
      const effectiveMin = computeEffectiveMin(item);
      const low = effectiveMin > 0 && item.current_qty < effectiveMin;
      return {
        ...item,
        min_qty: effectiveMin,
        status: low ? 'Low' : 'OK',
      };
    });
  }, [lowStockItems, computeEffectiveMin]);

  useEffect(() => {
    let filtered = [...itemsWithStatus];
    if (selectedProductId) {
      filtered = filtered.filter(item => item.product_id?._id === selectedProductId);
    }
    if (statusFilter === 'low') {
      filtered = filtered.filter(item => item.status === 'Low');
    } else if (statusFilter === 'ok') {
      filtered = filtered.filter(item => item.status === 'OK');
    }
    setFilteredData(filtered);
  }, [statusFilter, selectedProductId, itemsWithStatus]);

  useEffect(() => {
    fetchCompany();
    fetchProducts();
    fetchProductReorderMap();
  }, [fetchCompany, fetchProducts, fetchProductReorderMap]);

  useEffect(() => {
    fetchLowStock();
  }, [fetchLowStock]);

  const handleGenerate = () => {
    fetchLowStock();
    fetchProductReorderMap();
  };

  // ✅ Single print handler — uses the browser's native print with the
  //    .print-only block below. No more new-window duplication.
  const handlePrint = () => window.print();

  const formatNumber = (num) => (num !== undefined ? num.toLocaleString() : '0');
  const primaryColor = '#2e3192';
  const secondaryColor = '#f1592a';

  const grandTotalValue = filteredData.reduce((sum, item) => sum + (item.total_value || 0), 0);
  const lowStockOnlyCount = itemsWithStatus.filter(i => i.status === 'Low').length;

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  // ✅ Phone string (1, 2, 3) + fiscal year + currency for the A4 header
  const phoneLine = [
    company?.contact,
    company?.contact_2,
    company?.contact_3,
  ].filter(Boolean).join(' · ');

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  const rangeLabel = (() => {
    if (fromDate && toDate) {
      return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    }
    if (fromDate) return `From ${new Date(fromDate).toLocaleDateString()}`;
    if (toDate) return `Until ${new Date(toDate).toLocaleDateString()}`;
    return 'All Time';
  })();

  if (loadingCompany) {
    return <div style={{ color: secondaryColor, textAlign: 'center', marginTop: '2rem' }}>Loading company data...</div>;
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ==================================================
          SCREEN PREVIEW
      ================================================== */}
      <div className="screen-only" ref={printRef}>
        {/* HEADER */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {getLogoUrl() && (
                <img
                  src={getLogoUrl()}
                  alt="Logo"
                  style={{ height: '64px', maxWidth: '80px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: primaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>{company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{company?.address || 'No address'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {company?.contact || 'No phone'}
                {company?.contact_2 ? ` · ${company.contact_2}` : ''}
                {company?.contact_3 ? ` · ${company.contact_3}` : ''}
                <br />
                ✉️ {company?.admin_email || 'No email'}
              </p>
              <p>Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}</p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e9ecef', paddingTop: '0.5rem' }}>
            <h2 style={{ textAlign: 'center', color: secondaryColor }}>Low Stock Report ( below Reorder level )</h2>
            <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.8rem', marginTop: '4px' }}>
              {lowStockOnlyCount} item(s) below reorder level
            </p>
          </div>
        </div>

        {/* FILTERS (no-print) */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>Product</label>
              <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }}>
                <option value="">— All Items —</option>
                {productsList.map(prod => (
                  <option key={prod._id} value={prod._id}>{prod.product_code} - {prod.product_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }}>
                <option value="all">All</option>
                <option value="low">Low Stock Only (below Reorder)</option>
                <option value="ok">OK Stock Only</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleGenerate} disabled={loading} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', padding: '0.5rem', borderRadius: '0.375rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>{loading ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={filteredData.length === 0} style={{ backgroundColor: secondaryColor, color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredData.length === 0 ? 0.6 : 1 }}>🖨️ Print</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>
            * Low stock is defined as current available quantity strictly less than the product&apos;s <strong>Reorder Level</strong> (falls back to stock Min Qty).
          </div>
        </div>

        {/* TABLE */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
          {error && <div style={{ padding: '1rem', color: 'red', textAlign: 'center' }}>{error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: primaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Item Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Current Qty</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Min Qty (Reorder)</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Avg Cost</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Value</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && !loading && (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No items found. Click Generate Report.</td>
                    </tr>
                  )}
                  {filteredData.map((item, idx) => (
                    <tr key={item._id || item.product_id?._id || idx} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ padding: '0.75rem' }}>{item.item_name}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatNumber(item.current_qty)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatNumber(item.min_qty)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.avg_cost)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.total_value)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          backgroundColor: item.status === 'Low' ? '#dc3545' : '#28a745',
                          color: 'white'
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="5" style={{ padding: '0.75rem', textAlign: 'right' }}>Grand Total Value:</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(grandTotalValue)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {getLogoUrl() && (
              <img
                src={getLogoUrl()}
                alt="Logo"
                className="print-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || 'Company Name'}
              </h1>

              {/* ✅ Address now shown */}
              <p className="print-company-line">{company?.address || 'No address'}</p>

              {/* ✅ Phone 1 · Phone 2 · Phone 3 */}
              {phoneLine && (
                <p className="print-company-line">Tel: {phoneLine}</p>
              )}

              <p className="print-company-line">
                {company?.admin_email ? `Email: ${company.admin_email}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">LOW STOCK REPORT</h2>
          <p className="print-report-subtitle">
            {rangeLabel} · {lowStockOnlyCount} item(s) below reorder level
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th className="print-col-name">Item Name</th>
              <th className="print-col-qty">Current Qty</th>
              <th className="print-col-qty">Min Qty (Reorder)</th>
              <th className="print-col-money">Avg Cost</th>
              <th className="print-col-money">Total Value</th>
              <th className="print-col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="7" className="print-empty">
                  No items found for the selected filters.
                </td>
              </tr>
            ) : (
              filteredData.map((item, idx) => (
                <tr key={item._id || `p-${idx}`}>
                  <td className="print-col-no">{idx + 1}</td>
                  <td className="print-col-name">{item.item_name}</td>
                  <td className="print-col-qty">{formatNumber(item.current_qty)}</td>
                  <td className="print-col-qty">{formatNumber(item.min_qty)}</td>
                  <td className="print-col-money">{formatCurrency(item.avg_cost)}</td>
                  <td className="print-col-money">{formatCurrency(item.total_value)}</td>
                  <td className="print-col-status">
                    <span className={item.status === 'Low' ? 'print-status-low' : 'print-status-ok'}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
            {filteredData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="5" className="print-total-label">GRAND TOTAL VALUE</td>
                <td className="print-col-money print-total-value">{formatCurrency(grandTotalValue)}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ==================================================
          INLINE PRINT STYLES
      ================================================== */}
      <style>{`
        .print-only { display: none; }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, search, filters) */
          body * { visibility: hidden !important; }

          /* Show only the print block */
          .print-only,
          .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only,
          .no-print,
          .hide-on-print { display: none !important; }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 5mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 5mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999;
            padding: 3.5pt 5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 8.5pt;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }
          .print-col-no     { width: 10mm; text-align: center; }
          .print-col-qty    { width: 22mm; text-align: right; }
          .print-col-money  { width: 26mm; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
          .print-col-status { width: 20mm; text-align: center; }

          .print-status-low { color: #b91c1c; font-weight: 700; }
          .print-status-ok  { color: #15803d; font-weight: 700; }

          .print-total-row { background: #f4f4f4 !important; font-weight: 700; }
          .print-total-label { text-align: right; letter-spacing: 0.5pt; }
          .print-total-value { font-weight: 700; }
          .print-empty { text-align: center; padding: 12pt; font-style: italic; }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 10mm; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 5mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default LowStockReport;
